import { useState, useEffect } from 'react';

// Theme Hook
export const useTheme = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('sc-theme');
    return saved !== null ? saved === 'dark' : true; // default dark
  });

  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      localStorage.setItem('sc-theme', next ? 'dark' : 'light');
      return next;
    });
  };

  const getThemeClasses = () => {
    if (isDarkMode) {
      return {
        body: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white',
        topBar: 'bg-slate-800/80 border-slate-700/50 text-gray-300',
        header: 'bg-slate-800/70 border-slate-700/50',
        card: 'bg-slate-800/40 border-slate-700/50',
        hoverCard: 'hover:bg-slate-800/60',
        text: {
          primary: 'text-white',
          secondary: 'text-gray-300', 
          muted: 'text-gray-400',
          link: 'text-gray-400 hover:text-white'
        },
        input: 'bg-slate-700/50 border-slate-600 text-white placeholder-gray-400',
        button: 'bg-slate-800/60 hover:bg-slate-800/80 border-slate-700/50',
        menu: 'bg-slate-700/50 border-slate-600',
        chatBg: 'bg-slate-800/90 border-slate-700/50',
        messageInput: 'bg-slate-700/50 border-slate-600',
        userMessage: 'bg-pink-500',
        supportMessage: 'bg-slate-700/50 text-gray-300'
      };
    } else {
      return {
        body: 'bg-slate-100 text-slate-900',
        topBar: 'bg-gray-200/90 border-gray-300 text-gray-600',
        header: 'bg-gray-200/90 border-gray-300',
        card: 'bg-white/60 border-gray-200',
        hoverCard: 'hover:bg-white/80',
        text: {
          primary: 'text-slate-900',
          secondary: 'text-gray-700',
          muted: 'text-gray-500', 
          link: 'text-gray-600 hover:text-slate-900'
        },
        input: 'bg-white/80 border border-gray-200 text-slate-900 placeholder-gray-500',
        button: 'bg-gray-200/60 hover:bg-gray-200/80 border-gray-300',
        menu: 'bg-gray-200/90 border-gray-300',
        chatBg: 'bg-white/95 border-gray-200',
        messageInput: 'bg-gray-50 border border-gray-200',
        userMessage: 'bg-pink-500',
        supportMessage: 'bg-gray-100 text-gray-800 border border-gray-200'
      };
    }
  };

  return {
    isDarkMode,
    toggleTheme,
    theme: getThemeClasses()
  };
};

// Server Status Hook
export const useServerStatus = () => {
  const [serverStatus, setServerStatus] = useState({
    status: 'Status',
    uptime: 'Loading...',
    uptimePercentage: null,
    isOnline: true
  });

  const [uptimeData, setUptimeData] = useState({
    percentage: 99.9,
    days: 1,
    hours: 2,
    minutes: 30,
    availability: 99.9,
    responseTime: '24ms',
    requests: '1.2k',
    memory: '8GB',
    loading: true
  });

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  const fetchServices = async () => {
    try {
      const response = await fetch('/api/better-stack.php');
      const data = await response.json();
      if (data.success && data.monitors) {
        setServices(data.monitors.map(m => ({
          name: m.name,
          url: m.url,
          status: m.status,
          uptime: m.availability ?? '—',
          responseTime: m.response_time ?? '—',
          bars: m.bars ?? [],
        })));
      }
    } catch (error) {
      console.log('Better Stack API not available');
    } finally {
      setServicesLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
    const interval = setInterval(fetchServices, 60000);
    return () => clearInterval(interval);
  }, []);

  // Update isOnline based on Better Stack monitor statuses
  useEffect(() => {
    if (services.length === 0) return;
    const anyDown = services.some(s => s.status === 'offline');
    const anyDegraded = services.some(s => s.status !== 'online' && s.status !== 'offline');
    setServerStatus(prev => ({
      ...prev,
      isOnline: !anyDown,
      isDegraded: anyDegraded && !anyDown,
    }));
  }, [services]);

  const fetchUptimeData = async () => {
    try {
      const response = await fetch('/api/uptime.php');
      const text = await response.text();
      
      if (response.ok && text) {
        const uptimeMatch = text.match(/(\d+)d\s+(\d+)h(?:\s+(\d+)m)?/);
        if (uptimeMatch) {
          const days = parseInt(uptimeMatch[1]);
          const hours = parseInt(uptimeMatch[2]);
          const minutes = uptimeMatch[3] ? parseInt(uptimeMatch[3]) : 0;
          
          const totalUptimeSeconds = (days * 86400) + (hours * 3600) + (minutes * 60);
          const thirtyDaysSeconds = 30 * 86400;
          const uptimePercentage = Math.min(100, (totalUptimeSeconds / thirtyDaysSeconds) * 100);
          
          setUptimeData({
            percentage: uptimePercentage.toFixed(1),
            days: days,
            hours: hours,
            minutes: minutes,
            availability: uptimePercentage.toFixed(1),
            responseTime: '24ms',
            requests: '1.2k',
            memory: '8GB',
            loading: false
          });
          
          const uptimeString = `${days}d ${hours}h ${minutes}m`;
          
          setServerStatus({
            status: 'Status',
            uptime: uptimeString,
            uptimePercentage: uptimePercentage.toFixed(1),
            isOnline: true
          });
        }
      }
    } catch (error) {
      console.log('Using default uptime data - uptime API not available');
    }
  };

  useEffect(() => {
    fetchUptimeData();
    const interval = setInterval(fetchUptimeData, 30000);
    return () => clearInterval(interval);
  }, []);

  return {
    serverStatus,
    uptimeData,
    services,
    servicesLoading
  };
};

// Chat Hook
export const useChat = (clerkUser) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatClosing, setChatClosing] = useState(false);

  const getUserName = () => {
    if (clerkUser?.firstName) return `${clerkUser.firstName}${clerkUser.lastName ? ' ' + clerkUser.lastName : ''}`.trim();
    return 'Website Visitor';
  };

  const getUserEmail = () => clerkUser?.primaryEmailAddress?.emailAddress || '';

  const getGreeting = () => {
    if (clerkUser?.firstName) return `Welcome back, ${clerkUser.firstName}! How can we help you today?`;
    return "Hi! How can we help you today?";
  };

  const [messages, setMessages] = useState([
    { id: 1, text: getGreeting(), sender: "support", timestamp: new Date() }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [chatSession, setChatSession] = useState({
    sessionId: null,
    userName: getUserName(),
    userEmail: getUserEmail()
  });

  // Update session if user logs in mid-session
  useEffect(() => {
    setChatSession(prev => ({
      ...prev,
      userName: getUserName(),
      userEmail: getUserEmail()
    }));
  }, [clerkUser]);

  const handleChatToggle = () => {
    if (isChatOpen) {
      setChatClosing(true);
      setIsChatOpen(false);
      setTimeout(() => {
        setChatClosing(false);
      }, 300);
    } else {
      setIsChatOpen(true);
    }
  };

  const sendMessage = async () => {
    if (newMessage.trim()) {
      const userMessage = {
        id: messages.length + 1,
        text: newMessage,
        sender: "user",
        timestamp: new Date()
      };
      
      setMessages([...messages, userMessage]);
      const messageToSend = newMessage;
      setNewMessage('');

      // Always derive name/email from clerkUser at send time so late-loading Clerk is handled
      const currentName = clerkUser?.firstName
        ? `${clerkUser.firstName}${clerkUser.lastName ? ' ' + clerkUser.lastName : ''}`.trim()
        : chatSession.userName;
      const currentEmail = clerkUser?.primaryEmailAddress?.emailAddress || chatSession.userEmail;
      const isKnownUser = !!(clerkUser?.primaryEmailAddress?.emailAddress);
      
      try {
        const response = await fetch('/api/chat-send.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: chatSession.sessionId,
            user_name: currentName,
            user_email: currentEmail,
            message: messageToSend
          })
        });

        const result = await response.json();
        
        if (result.success) {
          if (result.session_id && !chatSession.sessionId) {
            setChatSession(prev => ({ ...prev, sessionId: result.session_id }));
          }
          
          setTimeout(() => {
            setMessages(prev => [...prev, {
              id: prev.length + 1,
              text: isKnownUser
                ? "Thanks for your message! Someone will be with you as soon as possible."
                : "Thanks for reaching out! Someone will be with you as soon as possible. If you don't mind, please share your name and email so we can follow up with you in case you need to leave.",
              sender: "support",
              timestamp: new Date()
            }]);
          }, 500);
        } else {
          throw new Error(result.error || 'Failed to send message');
        }
      } catch (error) {
        console.error('Error sending message:', error);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            id: prev.length + 1,
            text: "Sorry, there was an error sending your message. Please try again.",
            sender: "support",
            timestamp: new Date()
          }]);
        }, 500);
      }
    }
  };

  return {
    isChatOpen,
    chatClosing,
    messages,
    newMessage,
    setNewMessage,
    handleChatToggle,
    sendMessage
  };
};

// Contact Form Hook
export const useContactForm = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const handleContactFormChange = (field, value) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const handleContactSubmit = async () => {
    const trimmedForm = {
      name: contactForm.name.trim(),
      email: contactForm.email.trim(),
      subject: contactForm.subject.trim(),
      message: contactForm.message.trim()
    };

    if (!trimmedForm.name || !trimmedForm.email || !trimmedForm.subject || !trimmedForm.message) {
      showNotification('Please fill in all fields', 'error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedForm.email)) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    if (trimmedForm.name.length < 2) {
      showNotification('Please enter a valid name (at least 2 characters)', 'error');
      return;
    }

    if (trimmedForm.subject.length < 3) {
      showNotification('Please enter a subject (at least 3 characters)', 'error');
      return;
    }

    if (trimmedForm.message.length < 10) {
      showNotification('Please enter a message (at least 10 characters)', 'error');
      return;
    }

    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trimmedForm)
      });

      const result = await response.json();

      if (result.success) {
        setContactForm({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        showNotification('Thank you for your message! We\'ll get back to you soon. 💕', 'success');
      } else {
        showNotification('Sorry, there was an error sending your message. Please try again or contact us directly.', 'error');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification('Sorry, there was an error sending your message. Please try again or contact us directly.', 'error');
    }
  };

  return {
    contactForm,
    notification,
    setNotification,
    handleContactFormChange,
    handleContactSubmit
  };
};

// Portfolio Animations Hook
export const usePortfolioAnimations = (portfolioItems, liveEventsPortfolioItems, promotionsPortfolioItems, musicPortfolioItems) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [particles, setParticles] = useState({});
  const [particleTimeouts, setParticleTimeouts] = useState({});

  const createParticles = (itemId) => {
    if (particleTimeouts[itemId]) {
      clearTimeout(particleTimeouts[itemId]);
    }

    const timestamp = Date.now();
    const newParticles = [];
    for (let i = 0; i < 8; i++) {
      newParticles.push({
        id: timestamp + i + Math.random(),
        x: Math.random() * 80 + 10,
        y: Math.random() * 20 + 60,
        opacity: 1,
        size: Math.random() * 3 + 2,
      });
    }
    
    setParticles(prev => ({ ...prev, [itemId]: newParticles }));
    
    const timeoutId = setTimeout(() => {
      setParticles(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      setParticleTimeouts(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    }, 2000);

    setParticleTimeouts(prev => ({ ...prev, [itemId]: timeoutId }));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prevState => {
        const newState = { ...prevState };
        [...portfolioItems, ...liveEventsPortfolioItems, ...promotionsPortfolioItems, ...musicPortfolioItems].forEach(item => {
          if (item.images.length > 1) {
            const currentIndex = prevState[item.id] || 0;
            newState[item.id] = (currentIndex + 1) % item.images.length;
          }
        });
        return newState;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [portfolioItems, liveEventsPortfolioItems, promotionsPortfolioItems, musicPortfolioItems]);

  return {
    hoveredItem,
    setHoveredItem,
    currentImageIndex,
    particles,
    createParticles
  };
};